import { ReactElement, ReactNode } from 'react';
import { View } from 'react-native';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Button, IButtonProps } from '@/common/components/Button/Button';
import { Heading } from '@/common/components/Heading/Heading';
import { IconBadge } from '@/common/components/IconBadge/IconBadge';
import { TIconProps } from '@/common/components/Icons/Icon';
import { useOverlay } from '@/common/components/Overlay/Overlay.context';
import { TOverlayProps } from '@/common/components/Overlay/Overlay.types';
import { OverlayBottom } from '@/common/components/Overlay/OverlayBottom/OverlayBottom';
import { OverlayCenter } from '@/common/components/Overlay/OverlayCenter/OverlayCenter';
import { Stack } from '@/common/components/Stack/Stack';
import { useBreakpoints } from '@/common/hooks/useBreakpoints';
import { TColors } from '@coverflex-tech/hypernova-design-tokens';
import { styles } from './Dialog.styles';

export const Dialog = ({
  icon,
  iconBadgeColor,
  title,
  message,
  secondaryAction,
  primaryAction,
  name = 'dialog',
  ...overlayProps
}: TDialogProps) => {
  const breakpoints = useBreakpoints(['sm']);

  if (breakpoints.sm) {
    return (
      <OverlayCenter
        {...overlayProps}
        height="auto"
        horizontalPosition="center"
        closeOnOutsideClick={false}
        name={name}
      >
        <DialogContent
          icon={icon}
          iconBadgeColor={iconBadgeColor}
          title={title}
          message={message}
          secondaryAction={secondaryAction}
          primaryAction={primaryAction}
        />
      </OverlayCenter>
    );
  }

  return (
    <OverlayBottom
      {...overlayProps}
      closeOnOutsideClick={false}
      header={<View />}
      name={name}
    >
      <DialogContent
        icon={icon}
        iconBadgeColor={iconBadgeColor}
        title={title}
        message={message}
        secondaryAction={secondaryAction}
        primaryAction={primaryAction}
      />
    </OverlayBottom>
  );
};

const DialogContent = ({
  icon,
  iconBadgeColor,
  title,
  message,
  secondaryAction,
  primaryAction,
}: TDialogContentProps) => {
  const { close: closeDialog } = useOverlay();
  return (
    <Stack align="center" className={styles.content}>
      {icon && (
        <IconBadge
          ratio={56}
          icon={<icon.type {...icon.props} size={24} />}
          backgroundColor={iconBadgeColor}
        />
      )}
      <Stack gap="sm">
        <Heading level={3} className={styles.text}>
          {title}
        </Heading>
        <BodyText className={styles.text}>{message}</BodyText>
      </Stack>
      <Stack gap="md" className={styles.actions}>
        <Button
          {...primaryAction}
          onPress={() => {
            if (!primaryAction.onPress) {
              closeDialog();
            } else {
              primaryAction.onPress?.(closeDialog);
            }
          }}
          className={styles.primaryBtn}
        />
        {secondaryAction && (
          <Button
            variant="secondary"
            {...secondaryAction}
            onPress={() => {
              if (!secondaryAction.onPress) {
                closeDialog();
              } else {
                secondaryAction.onPress?.(closeDialog);
              }
            }}
            className={styles.secondaryBtn}
          />
        )}
      </Stack>
    </Stack>
  );
};

export interface TDialogProps
  extends Omit<
    TOverlayProps,
    | 'rootId'
    | 'height'
    | 'horizontalPosition'
    | 'children'
    | 'closeOnOutsideClick'
  > {
  icon?: ReactElement<TIconProps>;
  iconBadgeColor?: keyof TColors['light']['semantic']['surface'];
  title?: string;
  message?: ReactNode;
  secondaryAction?: IDialogActionProps;
  primaryAction: IDialogActionProps;
}

export type TDialogContentProps = Pick<
  TDialogProps,
  | 'icon'
  | 'iconBadgeColor'
  | 'title'
  | 'message'
  | 'secondaryAction'
  | 'primaryAction'
>;

type IDialogActionProps = Omit<IButtonProps, 'onPress'> & {
  onPress?: (closeDialog: () => void) => void;
};
